import React from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, 
  Heart, 
  AlertTriangle, 
  Pill, 
  Activity, 
  Utensils, 
  Smile, 
  Brain,
  Info,
  Zap
} from 'lucide-react';

const AgentStatusIndicator = ({ 
  currentAgent, 
  isLoading, 
  error, 
  agentStats, 
  onShowDetails 
}) => {
  
  const getAgentIcon = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return <Heart className="w-4 h-4" />;
      case 'EmergencyAgent':
        return <AlertTriangle className="w-4 h-4" />;
      case 'MedicationAgent':
        return <Pill className="w-4 h-4" />;
      case 'VitalsMonitoringAgent':
        return <Activity className="w-4 h-4" />;
      case 'NutritionAgent':
        return <Utensils className="w-4 h-4" />;
      case 'MentalHealthAgent':
        return <Smile className="w-4 h-4" />;
      case 'HealthResearchAgent':
        return <Brain className="w-4 h-4" />;
      default:
        return <Bot className="w-4 h-4" />;
    }
  };

  const getAgentDisplayName = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return 'Sophira (Main)';
      case 'EmergencyAgent':
        return 'Emergency';
      case 'MedicationAgent':
        return 'Medication';
      case 'VitalsMonitoringAgent':
        return 'Vitals';
      case 'NutritionAgent':
        return 'Nutrition';
      case 'MentalHealthAgent':
        return 'Mental Health';
      case 'HealthResearchAgent':
        return 'Research';
      default:
        return agentName.replace('Agent', '');
    }
  };

  const getAgentColor = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return 'bg-blue-500';
      case 'EmergencyAgent':
        return 'bg-red-500';
      case 'MedicationAgent':
        return 'bg-purple-500';
      case 'VitalsMonitoringAgent':
        return 'bg-green-500';
      case 'NutritionAgent':
        return 'bg-orange-500';
      case 'MentalHealthAgent':
        return 'bg-pink-500';
      case 'HealthResearchAgent':
        return 'bg-indigo-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm border border-gray-200"
    >
      <div className="flex items-center space-x-2">
        {/* Agent Status Indicator */}
        <div className="relative">
          <div className={`p-2 rounded-full ${getAgentColor(currentAgent)} text-white`}>
            {getAgentIcon(currentAgent)}
          </div>
          
          {/* Loading indicator */}
          {isLoading && (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full flex items-center justify-center"
            >
              <Zap className="w-2 h-2 text-white" />
            </motion.div>
          )}
          
          {/* Error indicator */}
          {error && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">!</span>
            </div>
          )}
        </div>

        {/* Agent Info */}
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-800">
            {getAgentDisplayName(currentAgent)}
          </span>
          <span className="text-xs text-gray-500">
            {isLoading ? 'Processing...' : 
             error ? 'Error occurred' : 
             'Active'}
          </span>
        </div>
      </div>

      {/* Agent Details Button */}
      <button
        onClick={onShowDetails}
        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
        title="Agent details"
      >
        <Info className="w-4 h-4 text-gray-500" />
      </button>
    </motion.div>
  );
};

export default AgentStatusIndicator; 